import {default as classNames, default as classnames} from "classnames";
import {useEffect, useRef, useState} from "react";
import {Link} from "react-router";

import {Alert} from "@/components/alert/Alert.tsx";
import Button from "@/components/button/Button.tsx";
import {Card} from "@/components/card/Card.tsx";
import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {MonitoringServerSelect} from "@/components/monitoringServerSelect/MonitoringServerSelect.tsx";
import {URL_ORIGINAL_WEBSITE} from "@/lib/links.ts";
import {Service} from "@/lib/types/checks.ts";
import {MonitoringServer} from "@/lib/types/monitoring.ts";

import styles from "./TracerouteOutput.module.css";

const TRACEROUTE_URL = `${URL_ORIGINAL_WEBSITE}/devices/services/ajax/traceroute`;
const STATUS_RETRY_DELAY = 2000;
const STATUS_RETRY_MAX = STATUS_RETRY_DELAY * 30;

const FeatureNotAvailable = () => (
  <>
    <h5 className="heading fw-bold my-4">Traceroute</h5>
    <Card variant="white" className="mb-4">
      Use{" "}
      <a
        target="_blank"
        href="https://support.uptime.com/hc/en-us/articles/360004800699-Using-Real-Time-Analysis-with-Uptime-com#traceroute"
      >
        traceroute
      </a>{" "}
      to detect connection anomalies between Uptime.com and your service. Contact{" "}
      <Link to="/contact">support@uptime.com</Link> to add traceroute to your subscription.
    </Card>
  </>
);

enum TaskStatus {
  INITIAL = "INITIAL",
  NEW = "NEW",
  RUNNING = "RUNNING",
  COMPLETED = "COMPLETED",
  FAILED = "FAILED",
}

interface InitializationResults {
  task_id: number;
}

interface TracerouteStatus {
  status: TaskStatus;
  result: string;
  error: string;
}

interface TracerouteProps {
  service: Service;
  monitoringServers: MonitoringServer[];
  className?: string;
}

export const Traceroute = ({service, monitoringServers, className}: TracerouteProps) => {
  const {user, userHasFeature} = useCurrentUserContext();
  const [tracerouteRuntime, setTracerouteRuntime] = useState<number>(0);
  const [currentTaskId, setCurrentTaskId] = useState<number | null>(null);
  const [tracerouteStatus, setTracerouteStatus] = useState<TracerouteStatus>({
    status: TaskStatus.INITIAL,
    result: "",
    error: "",
  });
  const [selectedMonitoringServer, setSelectedMonitoringServer] = useState<MonitoringServer | null>(
    monitoringServers ? monitoringServers[0] : null
  );
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (textAreaRef.current) textAreaRef.current.scrollTop = textAreaRef.current.scrollHeight;
  }, [tracerouteStatus]);

  useEffect(() => {
    const pollResults = async (taskId: number) => {
      const response = await fetch(`${TRACEROUTE_URL}?task_id=${taskId}`, {
        method: "GET",
        credentials: "include",
        mode: "cors",
        headers: {
          "Content-Type": "application/json",
        },
      });

      const data = (await response.json()).data as TracerouteStatus;

      setTracerouteRuntime(tracerouteRuntime + STATUS_RETRY_DELAY);
      setTracerouteStatus(data);
    };

    if (tracerouteRuntime > STATUS_RETRY_MAX) {
      setTracerouteRuntime(0);
      setTracerouteStatus({
        status: TaskStatus.FAILED,
        error: "Task execution timeout",
        result: tracerouteStatus.result,
      });
    } else if ([TaskStatus.NEW, TaskStatus.RUNNING].includes(tracerouteStatus.status) && currentTaskId) {
      const timeoutId = setTimeout(() => pollResults(currentTaskId), STATUS_RETRY_DELAY);

      return () => clearTimeout(timeoutId);
    }
  }, [currentTaskId, tracerouteRuntime, tracerouteStatus]);

  if (!monitoringServers || monitoringServers.length < 1) return null;

  const beginTraceroute = async () => {
    const response = await fetch(TRACEROUTE_URL, {
      method: "POST",
      credentials: "include",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        service_id: service.id,
        server_id: selectedMonitoringServer!.id,
      }),
    });

    const data = (await response.json()).data as InitializationResults;

    setTracerouteRuntime(0);
    setTracerouteStatus({
      status: TaskStatus.NEW,
      result: "",
      error: "",
    });
    setCurrentTaskId(data.task_id);
  };

  if (!userHasFeature("traceroute")) return null;
  if (!user?.has_enterprise_features) return <FeatureNotAvailable />;

  const isActive = [TaskStatus.NEW, TaskStatus.RUNNING].includes(tracerouteStatus.status);
  const inQueue = tracerouteStatus.status === TaskStatus.NEW;
  const progress = (tracerouteRuntime / (STATUS_RETRY_MAX + STATUS_RETRY_DELAY)) * 100;

  return (
    <>
      <h5 className="heading fw-bold my-4">Traceroute</h5>
      <Card variant="white" className={classnames("mb-4", className)}>
        {!isActive && (
          <div className="row">
            <div className="col-auto align-content-center">Run a traceroute from this location:</div>
            <div className="col-auto">
              <MonitoringServerSelect
                name={"monitoring_server"}
                value={selectedMonitoringServer}
                onChange={setSelectedMonitoringServer}
                options={monitoringServers}
              />
            </div>
            <div className="col-auto">
              <Button onClick={() => beginTraceroute()}>Submit</Button>
            </div>
          </div>
        )}
        {tracerouteStatus.error && (
          <Alert variant={"error"} className="mt-3">
            {tracerouteStatus.error}
          </Alert>
        )}
        {isActive && (
          <>
            <div className="pb-2">
              Please wait...{" "}
              <span className="fw-bold">{inQueue ? "Task is waiting in queue." : "Task is in progress."}</span>
            </div>
            <div className="progress">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                role="progressbar"
                aria-valuenow={inQueue ? 0 : progress}
                aria-valuemin={0}
                aria-valuemax={100}
                style={{width: (inQueue ? 0 : progress) + "%"}}
              ></div>
            </div>
          </>
        )}
        {tracerouteStatus.status !== TaskStatus.INITIAL && (
          <div className="row m-1 mt-3">
            <textarea
              ref={textAreaRef}
              className={classNames("form-control", styles.linuxTerminalColors)}
              readOnly={true}
              rows={15}
              value={tracerouteStatus.result}
            />
          </div>
        )}
      </Card>
    </>
  );
};
